import React, { useEffect, useMemo, useState } from 'react';
import Modal from 'Components/Generic/Modal';
import { TFunction, useTranslation } from 'react-i18next';
import SvgIconUrl from '../../../helpers/SvgIcon/SvgIconUrl';
import { Grid } from '@mui/material';
import TextField from 'Components/Form/Input';
import { useAppState } from 'Context/StateContext';
import { EmailTemplateService } from 'Services/templates/email.template.service';
import { EmailTemplateToSend } from 'Models/templates/email.template';
import * as yup from 'yup';

import useYupValidation, { useHandleValidate, useYupSchemas } from 'Hooks/useYupValidation';

import {
  Body,
  ErrorContainer,
  ErrorIcon,
  FieldLabel,
  InformationCharactersLabel,
  MFormControl,
  ModalContainer,
  ModalContainerText,
  TextAreaField,
  TotalCharactersLabel,
} from './styles';

import { useMutation } from '@tanstack/react-query';
import { NotificationToast } from 'Components/Generic/Toast/NotificationToast';

interface SupportSendRequestPopupProps {
  isOpen: boolean;
  feature?: string;
  onClose?: () => void;
  onSuccess?: () => void;
}

const sendIcon = require('Assets/icons/send-2.svg');

const MESSAGE_LIMIT = 500;
const MIN_CHARS = 50;
const SUPPORTEMAIL = 'support@shipeezi.com';

const limitedCharacters = (typed: number, limit: number, t: TFunction) =>
  `${typed} / ${limit} ${t('characters')}`;

export const SupportSendRequestPopup = (props: SupportSendRequestPopupProps) => {
  const { isOpen, feature, onClose, onSuccess } = props;
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const [message, setMessage] = useState<string>('');
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);

  const { fieldHasError, getErrorByField } = useYupValidation(errors);
  const { stringRequired } = useYupSchemas(translate);

  useEffect(() => {
    if (isSubmitted) handleValidate();
  }, [message]);

  const schema = useMemo(
    () =>
      yup.object().shape({
        message: stringRequired
          .transform((v) => (typeof v === 'string' ? v : ''))
          .trim()
          .min(MIN_CHARS, translate('minCharacters', { count: MIN_CHARS }))
          .max(MESSAGE_LIMIT, translate('maxCharacters', { count: MESSAGE_LIMIT })),
      }),
    [stringRequired, translate],
  );

  const handleValidate = () => {
    return useHandleValidate({ message }, schema, setErrors);
  };

  const handleMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value.slice(0, MESSAGE_LIMIT);
    setMessage(v);
  };

  const { mutate: sendEmail, isLoading } = useMutation({
    mutationKey: ['send.email'],
    mutationFn: async (emailToSend: EmailTemplateToSend) =>
      await EmailTemplateService.send(emailToSend),
    onSuccess: () => {
      NotificationToast.success(translate('yourRequestHasBeenSentOurSupportTeam'));
      onSuccess?.();
      onClose?.();
    },
    onError: () => {
      NotificationToast.error(translate('ohNoSomethingWentWrongPleaseTryAgain'));
    },
  });

  const buildSubject = () => `Feature access request${feature ? `: ${feature}` : ''}`;

  const buildHtml = () => {
    const name = currentUser?.fullName ?? '';
    const email = currentUser?.email ?? '';
    const company = currentUser?.company?.name ?? '';
    const feat = feature ?? '';
    const msg = message.trim();

    return `
      <div>
        <p><strong>Name:</strong> ${name}</p>
        <p><strong>Email:</strong> ${email}</p>
        <p><strong>Company:</strong> ${company}</p>
        <p><strong>Feature:</strong> ${feat}</p>
        <p><strong>Message:</strong></p>
        <pre style="white-space:pre-wrap;margin:0;">${msg}</pre>
      </div>
    `;
  };

  const handleSendRequest = async () => {
    setIsSubmitted(true);
    if (!handleValidate()) return;

    const mail: EmailTemplateToSend = {
      name: currentUser?.company?.name ?? '',
      from: currentUser?.email ?? '',
      to: SUPPORTEMAIL,
      html: buildHtml(),
      subject: buildSubject(),
      cc: '',
      bcc: '',
    };

    sendEmail(mail);
  };

  const renderSupportRequest = () => {
    const totalCharacters = message.length;

    return (
      <ModalContainer>
        <ModalContainerText>
          {translate('itLooksLikeYouDontHaveAcessThisFeatureYet')}
        </ModalContainerText>

        <Body>
          <Grid container spacing={2} direction="row" style={{ marginBottom: '24px' }}>
            <Grid item xs={12} md={6} lg={6}>
              <MFormControl>
                <FieldLabel>{translate('name')}</FieldLabel>
                <TextField
                  name="name"
                  data-cy="name"
                  placeholder={translate('name')}
                  value={currentUser?.name}
                  disabled
                />
              </MFormControl>
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <MFormControl>
                <FieldLabel>{translate('email')}</FieldLabel>
                <TextField
                  name="email"
                  data-cy="email"
                  placeholder={translate('email')}
                  value={currentUser?.email}
                  disabled
                />
              </MFormControl>
            </Grid>
          </Grid>

          <Grid container spacing={2} direction="row" style={{ marginBottom: '24px' }}>
            <Grid item xs={12} md={6} lg={6}>
              <MFormControl>
                <FieldLabel>{translate('company')}</FieldLabel>
                <TextField
                  name="company"
                  data-cy="company"
                  placeholder={translate('company')}
                  value={currentUser?.company?.name}
                  disabled
                />
              </MFormControl>
            </Grid>

            <Grid item xs={12} md={6} lg={6}>
              <MFormControl>
                <FieldLabel>{translate('feature')}</FieldLabel>
                <TextField
                  name="feature"
                  data-cy="feature"
                  placeholder={translate('feature')}
                  value={feature}
                  disabled
                />
              </MFormControl>
            </Grid>
          </Grid>

          <Grid container item xs={12}>
            <MFormControl error={fieldHasError('message')}>
              <FieldLabel style={{ color: '#0055A4' }}>{translate('message')}</FieldLabel>

              <TextAreaField
                name="message"
                data-cy="message"
                placeholder={translate('pleaseDescribeYouIntendUseFeature')}
                value={message}
                onChange={handleMessageChange}
                multiline
                minRows={6}
                maxRows={10}
                inputProps={{ maxLength: MESSAGE_LIMIT }}
              />

              {fieldHasError('message') && (
                <ErrorContainer>
                  <ErrorIcon fontSize="inherit" />
                  {getErrorByField('message')}
                </ErrorContainer>
              )}
            </MFormControl>

            <TotalCharactersLabel>
              {limitedCharacters(totalCharacters, MESSAGE_LIMIT, translate)}
            </TotalCharactersLabel>

            <Grid item xs={12}>
              <InformationCharactersLabel>
                {translate('yourMessageShouldLeast50Characters')}
              </InformationCharactersLabel>
            </Grid>
          </Grid>
        </Body>
      </ModalContainer>
    );
  };

  return (
    <Modal
      open={isOpen}
      handleClose={onClose}
      title={translate('titleFeatureRequestSupport')}
      showActionButtons
      actionButtonProps={{
        onSave: handleSendRequest,
        onCancel: onClose,
        textSave: isLoading ? translate('sending') : translate('sendRequestSupport'),
        textCancel: translate('cancel'),
        saveIcon: <SvgIconUrl src={sendIcon} size={16} color="#fff" />,
        loading: isLoading,
        saveDisabled: isLoading,
      }}
      maxWidth={910}
      width="50%"
    >
      {renderSupportRequest()}
    </Modal>
  );
};
